// The chat POST lifecycle, split from the controller so request preparation, Session minting and uncertain
// transport retry each have one bounded seam. The browser request id is correlation only: the server still
// authenticates the Actor, owns the Session and durably binds the exact message before a Run may start.

import { useCallback } from 'react';
import type { SurfaceApi } from './api-client.ts';
import {
  mintChatRequestId,
  reusablePendingChatRequest,
  type PendingChatRequest,
} from './chat-request-identity.ts';
import { writeResumePointer } from './chat-resume.ts';
import * as chatSession from './chat-session-state.ts';
import { openStream, type ChatStreamStores } from './chat-stream-lifecycle.ts';
import type { ChatTurn } from './chat-turns.ts';
import type { ChatSendResult, ChatTranscriptView } from './projections.ts';

type ChatSendApi = Pick<
  SurfaceApi,
  'createChatSession' | 'sendChatMessage' | 'getChatTranscript' | 'streamChat'
>;

export interface ChatSendStores extends ChatStreamStores {
  readonly sessionIdRef: React.MutableRefObject<string | null>;
  readonly sending: boolean;
  readonly clearTranscript: () => void;
  readonly setSending: (value: boolean) => void;
  readonly setNote: (note: string | null) => void;
  readonly sendGenRef: React.MutableRefObject<number>;
  readonly pendingRequestRef: React.MutableRefObject<PendingChatRequest | null>;
  readonly transcriptRef: React.MutableRefObject<ChatTranscriptView | null>;
  readonly setPriorRuns: React.Dispatch<React.SetStateAction<readonly ChatTranscriptView[]>>;
  readonly setActiveSessionId: (sessionId: string | null) => void;
}

interface ChatSendAttempt {
  readonly text: string;
  readonly sendGeneration: number;
  readonly pending: PendingChatRequest;
  readonly localTurnId: string;
}

/** Prepare one exact retry identity and preserve the human's own turn before any transport begins. */
function prepareChatSend(message: string, stores: ChatSendStores): ChatSendAttempt | undefined {
  const text = message.trim();
  if (text.length === 0 || stores.sending) return undefined;
  const sendGeneration = (stores.sendGenRef.current += 1);
  const reusable = reusablePendingChatRequest(
    stores.pendingRequestRef.current,
    stores.sessionIdRef.current,
    text,
  );
  const localTurnId = reusable?.localTurnId ?? chatSession.nextLocalTurnId();
  let pending: PendingChatRequest;
  try {
    pending =
      reusable ??
      ({
        requestId: mintChatRequestId(),
        message: text,
        localTurnId,
        sessionId: stores.sessionIdRef.current,
      } satisfies PendingChatRequest);
  } catch {
    stores.setNote('A secure chat request could not be prepared.');
    return undefined;
  }
  stores.pendingRequestRef.current = pending;
  if (reusable === undefined) {
    stores.setLocalTurns((previous: readonly ChatTurn[]) => [
      ...previous,
      { id: localTurnId, author: 'human', text },
    ]);
  }
  stores.setSending(true);
  stores.setNote(null);
  return { text, sendGeneration, pending, localTurnId };
}

/** Resolve the platform-owned Session, abandoning a mint superseded by a conversation switch. */
async function resolveChatSession(
  api: ChatSendApi,
  stores: ChatSendStores,
  attempt: ChatSendAttempt,
): Promise<string | undefined> {
  const existing = stores.sessionIdRef.current;
  if (existing !== null) return existing;
  try {
    const minted = await api.createChatSession();
    if (stores.sendGenRef.current !== attempt.sendGeneration) return undefined;
    stores.sessionIdRef.current = minted;
    attempt.pending.sessionId = minted;
    stores.setActiveSessionId(minted);
    return minted;
  } catch {
    if (stores.sendGenRef.current === attempt.sendGeneration) {
      stores.setSending(false);
      stores.setNote('The chat session could not be established.');
      stores.pendingRequestRef.current = null;
    }
    return undefined;
  }
}

/** Send once. A thrown transport leaves the exact pending identity for an explicit same-message retry. */
async function transmitChatSend(
  api: ChatSendApi,
  stores: ChatSendStores,
  attempt: ChatSendAttempt,
  sessionId: string,
): Promise<ChatSendResult | undefined> {
  let result: ChatSendResult;
  try {
    result = await api.sendChatMessage(sessionId, attempt.text, attempt.pending.requestId);
  } catch {
    if (stores.sendGenRef.current === attempt.sendGeneration && stores.mountedRef.current) {
      stores.setNote(chatSession.SEND_TRANSPORT_NOTE);
    }
    return undefined;
  } finally {
    if (stores.sendGenRef.current === attempt.sendGeneration && stores.mountedRef.current) {
      stores.setSending(false);
    }
  }
  if (stores.pendingRequestRef.current === attempt.pending) stores.pendingRequestRef.current = null;
  if (stores.sendGenRef.current !== attempt.sendGeneration || !stores.mountedRef.current) {
    return undefined;
  }
  return result;
}

/** Roll a resolved previous run forward exactly once when this send starts a new run. */
function rollPriorRunForward(stores: ChatSendStores, localTurnId: string, newRunId: string): void {
  const previous = stores.transcriptRef.current;
  if (previous !== null && previous.runId !== newRunId) {
    stores.setPriorRuns((prior) => [...prior, previous]);
    stores.clearTranscript();
  }
  stores.setLocalTurns((turns) =>
    turns.map((turn) => (turn.id === localTurnId ? { ...turn, pendingRunId: newRunId } : turn)),
  );
}

/** Apply only a recorded route outcome; agent-authored output still comes from stream/snapshot reads. */
function applyChatSendResult(
  api: ChatSendApi,
  result: ChatSendResult,
  stores: ChatSendStores,
  localTurnId: string,
): void {
  if (result.kind === 'started') {
    rollPriorRunForward(stores, localTurnId, result.runId);
    openStream(api, result.runId, stores);
  } else if (result.kind === 'queued') {
    stores.setNote(chatSession.QUEUED_NOTE);
  } else {
    stores.setNote(result.note ?? chatSession.REFUSED_NOTE);
    if (stores.sessionIdRef.current !== null) {
      writeResumePointer({ sessionId: stores.sessionIdRef.current });
    }
  }
}

/** Stable React action over the bounded preparation, Session and transport seams above. */
export function useChatSendAction(
  api: ChatSendApi,
  stores: ChatSendStores,
): (message: string) => Promise<void> {
  return useCallback(
    async (message: string): Promise<void> => {
      const attempt = prepareChatSend(message, stores);
      if (attempt === undefined) return;
      const sessionId = await resolveChatSession(api, stores, attempt);
      if (sessionId === undefined) return;
      const result = await transmitChatSend(api, stores, attempt, sessionId);
      if (result !== undefined) applyChatSendResult(api, result, stores, attempt.localTurnId);
    },
    [api, stores],
  );
}
